"use client";

import * as React from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Skeleton } from "@gerama/ui";

interface Page<T> {
  list: T[];
  count: number;
}

/**
 * Infinite scroll over a paged list endpoint: pages of `limit` load as the
 * sentinel nears the viewport. `hasNext` comes from the running total, so it
 * works for lists whose `count` is only "at least this many" too.
 */
export function useInfiniteList<T>({
  queryKey,
  fetchPage,
  limit = 24,
  enabled = true,
  staleTime,
}: {
  queryKey: readonly unknown[];
  fetchPage: (page: number, limit: number, signal: AbortSignal) => Promise<Page<T>>;
  limit?: number;
  enabled?: boolean;
  /** Override the app-wide 60s; `0` refetches whenever the list is opened again. */
  staleTime?: number;
}) {
  const query = useInfiniteQuery({
    queryKey: [...queryKey, limit],
    queryFn: ({ pageParam, signal }) => fetchPage(pageParam, limit, signal),
    initialPageParam: 1,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.list.length, 0);
      return last.list.length >= limit && loaded < last.count ? pages.length + 1 : undefined;
    },
    enabled,
    staleTime,
  });
  const items = React.useMemo(() => query.data?.pages.flatMap((p) => p.list) ?? [], [query.data]);
  return { ...query, items, total: query.data?.pages[0]?.count ?? 0 };
}

/** Default "loading more" placeholder: a few shimmering list rows. */
function MoreSkeleton() {
  return (
    <div className="w-full space-y-2" aria-hidden>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-2 py-1.5">
          <Skeleton className="size-11 shrink-0 rounded-md" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-2/5" />
            <Skeleton className="h-3 w-1/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Sits below a list; fetches the next page when scrolled near. While the next
 * page loads it shows a skeleton in the list's own shape (`skeleton`), or a few
 * generic rows — never a bare spinner.
 */
export function InfiniteSentinel({
  hasNext,
  loading,
  onLoadMore,
  skeleton,
}: {
  hasNext: boolean;
  loading: boolean;
  onLoadMore: () => void;
  skeleton?: React.ReactNode;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const load = React.useRef(onLoadMore);
  load.current = onLoadMore;

  React.useEffect(() => {
    const el = ref.current;
    if (!el || !hasNext) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !loading) load.current();
      },
      { rootMargin: "600px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNext, loading]);

  return (
    <div ref={ref} className="flex min-h-16 items-start justify-center pt-2" aria-busy={loading}>
      {loading && (skeleton ?? <MoreSkeleton />)}
    </div>
  );
}
